import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Bot,
  Cpu,
  Workflow,
  Terminal,
  Radio,
  ShieldCheck,
  Mail,
  MapPin,
  ExternalLink,
  ArrowRight,
  Award,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { SiteShell } from "@/components/SiteShell";
import { ArticleCard } from "@/components/articles";
import { fetchArticles } from "@/lib/articles";
import { fetchAuthorProfile, getAuthorInitials, getAuthorSocial } from "@/lib/author";
import ReactMarkdown from "react-markdown";

export const Route = createFileRoute("/author/jignesh-mevada")({
  loader: async () => {
    const [articles, profile] = await Promise.all([fetchArticles(), fetchAuthorProfile()]);
    return { articles, profile };
  },
  head: ({ loaderData }) => ({
    meta: [
      {
        title: `${loaderData?.profile.name ?? "Author"} | AI Systems Architect | BuildWithJIT`,
      },
      {
        name: "description",
        content: loaderData?.profile.seo_description ?? "Author profile at BuildWithJIT.",
      },
      {
        name: "robots",
        content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
      },
      {
        name: "googlebot",
        content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
      },
      { property: "og:site_name", content: "BuildWithJIT" },
      {
        property: "og:title",
        content: `${loaderData?.profile.name ?? "Author"} | AI Systems Architect | BuildWithJIT`,
      },
      {
        property: "og:description",
        content: loaderData?.profile.seo_description ?? "Author profile at BuildWithJIT.",
      },
      { property: "og:type", content: "profile" },
      { property: "og:url", content: "https://buildwithjit.com/author/jignesh-mevada" },
      { property: "og:image", content: "https://buildwithjit.com/og-share.png" },
      { property: "og:image:secure_url", content: "https://buildwithjit.com/og-share.png" },
      { property: "og:image:type", content: "image/png" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      {
        property: "og:image:alt",
        content: `${loaderData?.profile.name ?? "Author"}: Author Profile & Technical Publications`,
      },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:site", content: "@buildwithjit" },
      { name: "twitter:creator", content: "@buildwithjit" },
      {
        name: "twitter:title",
        content: `${loaderData?.profile.name ?? "Author"} | AI Systems Architect | BuildWithJIT`,
      },
      {
        name: "twitter:description",
        content: loaderData?.profile.seo_description ?? "Author profile at BuildWithJIT.",
      },
      { name: "twitter:image", content: "https://buildwithjit.com/og-share.png" },
    ],
    links: [
      { rel: "canonical", href: "https://buildwithjit.com/author/jignesh-mevada" },
      { rel: "icon", href: "/favicon.ico", sizes: "any" },
      { rel: "icon", type: "image/png", sizes: "48x48", href: "/icon-48.png" },
      { rel: "icon", type: "image/png", sizes: "96x96", href: "/icon-96.png" },
      { rel: "icon", type: "image/png", sizes: "192x192", href: "/icon-192.png" },
      { rel: "icon", type: "image/png", sizes: "512x512", href: "/icon-512.png" },
      { rel: "icon", type: "image/svg+xml", href: "/favicon.svg" },
      { rel: "shortcut icon", href: "/favicon.ico" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png" },
    ],
  }),
  component: AuthorPage,
});

function AuthorPage() {
  const { articles, profile } = Route.useLoaderData();
  const social = getAuthorSocial(profile);
  const specializationIcons = {
    cpu: Cpu,
    radio: Radio,
    workflow: Workflow,
    "shield-check": ShieldCheck,
  };

  const authorProfileSchema = {
    "@context": "https://schema.org",
    "@type": "Person",
    "@id": "https://buildwithjit.com/author/jignesh-mevada#person",
    name: profile.name,
    jobTitle: profile.job_title,
    image: "https://buildwithjit.com/icon-512.png",
    url: "https://buildwithjit.com/author/jignesh-mevada",
    worksFor: {
      "@type": "Organization",
      name: profile.company,
      url: "https://buildwithjit.com",
      logo: {
        "@type": "ImageObject",
        url: "https://buildwithjit.com/icon-512.png",
        width: 512,
        height: 512,
      },
      sameAs: Object.values(social),
    },
    address: {
      "@type": "PostalAddress",
      addressLocality: profile.location.split(",")[0]?.trim(),
      addressRegion: profile.location.split(",")[1]?.trim(),
      postalCode: profile.postal_code,
      addressCountry: profile.country_code,
    },
    email: profile.email,
    sameAs: Object.values(social),
    description: profile.profile_bio.replace(/\n+/g, " "),
  };

  return (
    <SiteShell social={social}>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(authorProfileSchema) }}
      />
      <div className="author-profile-page container-wide py-12 md:py-20 px-4 sm:px-6 max-w-5xl mx-auto">
        {/* Breadcrumbs */}
        <nav aria-label="Breadcrumb" className="mb-8 font-mono text-xs text-muted-foreground">
          <ol className="flex items-center gap-2">
            <li>
              <Link to="/" className="hover:text-primary transition-colors">
                Home
              </Link>
            </li>
            <li aria-hidden="true">/</li>
            <li>
              <Link to="/about" className="hover:text-primary transition-colors">
                About
              </Link>
            </li>
            <li aria-hidden="true">/</li>
            <li className="text-foreground font-medium" aria-current="page">
              Author Profile
            </li>
          </ol>
        </nav>

        {/* Hero Author Card */}
        <header className="p-8 md:p-12 rounded-3xl border border-border bg-card/60 backdrop-blur-md shadow-sm mb-16">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-8">
            <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-3xl bg-primary/10 border-2 border-primary/25 flex items-center justify-center text-primary font-bold text-3xl sm:text-4xl shadow-inner shrink-0">
              {getAuthorInitials(profile.name)}
            </div>
            <div className="flex-1 space-y-3">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary font-mono text-xs font-semibold uppercase tracking-wider">
                <Award size={13} /> {profile.headline}
              </div>
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground">
                {profile.name}
              </h1>
              <div className="text-base sm:text-lg text-muted-foreground leading-relaxed">
                <ReactMarkdown
                  components={{
                    p: ({ children }) => <p>{children}</p>,
                    strong: ({ children }) => (
                      <strong className="text-foreground">{children}</strong>
                    ),
                  }}
                >
                  {profile.profile_intro}
                </ReactMarkdown>
              </div>
              <div className="flex flex-wrap items-center gap-4 pt-2 text-sm">
                <a
                  href={social.linkedin}
                  target="_blank"
                  rel="me noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[#0A66C2] text-white font-medium hover:bg-[#0A66C2]/90 transition-all text-xs"
                >
                  LinkedIn Profile <ExternalLink size={13} />
                </a>
                <a
                  href={social.github}
                  target="_blank"
                  rel="me noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl border border-border bg-muted/60 text-foreground font-medium hover:bg-accent transition-all text-xs"
                >
                  GitHub Projects <ExternalLink size={13} />
                </a>
                <span className="flex items-center gap-1 text-xs text-muted-foreground font-mono">
                  <MapPin size={13} className="text-primary" /> {profile.location}
                </span>
              </div>
            </div>
          </div>
        </header>

        {/* Section: About the Author */}
        <section className="mb-16">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground mb-6">
            About the Author
          </h2>
          <div className="prose dark:prose-invert max-w-none text-muted-foreground space-y-4 text-base sm:text-lg leading-relaxed">
            <ReactMarkdown>{profile.profile_bio}</ReactMarkdown>
          </div>
        </section>

        {/* Section: Technical Specializations */}
        <section className="mb-16">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground mb-6">
            Areas of Specialization
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {profile.specializations.map((specialization) => {
              const Icon = specializationIcons[specialization.icon];
              return (
                <div
                  key={specialization.title}
                  className="p-5 rounded-2xl border border-border bg-card/40 space-y-2"
                >
                  <div className="flex items-center gap-2 text-primary font-bold">
                    <Icon size={18} /> {specialization.title}
                  </div>
                  <p className="text-sm text-muted-foreground">{specialization.description}</p>
                </div>
              );
            })}
          </div>
        </section>

        {/* Section: Articles by Author */}
        <section>
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground">
                Published Architecture Guides &amp; Articles
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                All {articles.length} in-depth technical guides authored by {profile.name}
              </p>
            </div>
            <Button asChild variant="outline" size="sm">
              <Link to="/articles">
                View Archive <ArrowRight size={14} className="ml-1" />
              </Link>
            </Button>
          </div>

          <div className="archive-grid">
            {articles.map((article) => (
              <ArticleCard key={article.slug} article={article} />
            ))}
          </div>
        </section>
      </div>
    </SiteShell>
  );
}
